import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  pointerWithin,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToWindowEdges } from '@dnd-kit/modifiers';
import {
  createContext,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
} from 'react';

import { ELayoutManagerMode, type EWidgetSection } from '#shared';

import { DragPreview, DeleteZoneOverlay, useToast } from '../../LayoutManager';
import { useAppDispatch } from '../../redux-store';
import { sectionSelectors, useIsApplying } from '../../slices';
import {
  type WidgetId,
  type WidgetType,
  widgetManagerActions,
  widgetRegistry,
  useSelectWidgetManagerDraggingWidget,
} from '../../widgetManager';

import { centerCursorModifier } from './dragModifiers';
import { useDragHandlers } from './useDragHandlers';
import { useTapHandlers } from './useTapHandlers';
import { extractBaseWidgetType } from './utils';

type PendingPlacement = {
  sectionId: EWidgetSection;
  position: number;
} | null;

/**
 * Unified context for DnD and Tap editing logic
 */
export type LayoutEditingContextProps = {
  // Mode & State
  mode: ELayoutManagerMode;

  // Tap Selection
  selectedWidgetId: WidgetId | null;
  setSelectedWidgetId: (widgetId: WidgetId | null) => void;
  selectedPaletteWidget: string | null; // Can be WidgetType or variant ID like 'geo-map-center'
  setSelectedPaletteWidget: (widgetId: string | null) => void;

  // Tap-to-Place
  handleTapPlace: (
    sectionId: EWidgetSection,
    position?: number,
    widgetId?: string,
  ) => void;
  handleTapReplace: (
    targetWidgetId: WidgetId,
    newWidgetVariantId: string,
  ) => void;
  handleTapSwap: (sourceWidgetId: WidgetId, targetWidgetId: WidgetId) => void;
  pendingPlacement: PendingPlacement;
  setPendingPlacement: (placement: PendingPlacement) => void;

  // Actions
  handleDeleteWidget: (widgetId: WidgetId) => void;

  // Toast notifications
  showToast: ReturnType<typeof useToast>['showToast'];
};

export const LayoutEditingContext = createContext<
  LayoutEditingContextProps | undefined
>(undefined);

export const useLayoutEditing = (): LayoutEditingContextProps => {
  const context = useContext(LayoutEditingContext);
  if (!context) {
    throw new Error(
      'useLayoutEditing must be used within LayoutEditingProvider',
    );
  }
  return context;
};

interface LayoutEditingProviderProps {
  mode: ELayoutManagerMode;
  onDeleteLastWidget?: () => void; // Callback when attempting to delete last widget
}

export const LayoutEditingProvider = ({
  children,
  mode,
  onDeleteLastWidget,
}: PropsWithChildren<LayoutEditingProviderProps>) => {
  const dispatch = useAppDispatch();
  const sectionsById = sectionSelectors.useSelectSectionEntities();
  const draggingWidget = useSelectWidgetManagerDraggingWidget();
  const isApplying = useIsApplying();
  const { showToast, ToastComponent } = useToast();

  // Sync mode to Redux so WidgetToolbar can detect VIEW/EDIT/RENDER mode
  useEffect(() => {
    dispatch(
      widgetManagerActions.setGeneralSettings({
        layoutManagerMode: mode,
      }),
    );
  }, [mode, dispatch]);

  // Tap/Selection handlers
  const {
    selectedWidgetId,
    setSelectedWidgetId,
    selectedPaletteWidget,
    setSelectedPaletteWidget,
    pendingPlacement,
    setPendingPlacement,
    handleTapPlace,
    handleTapReplace,
    handleDeleteWidget,
    handleTapSwap,
  } = useTapHandlers({
    mode,
    dispatch,
    sectionsById,
    onDeleteLastWidget,
  });

  // Drag & Drop handlers
  const {
    activeId,
    isDragging,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
  } = useDragHandlers({
    dispatch,
    sectionsById,
    draggingWidget,
    onDeleteLastWidget,
    showToast,
  });

  // Configure sensors with distance-based activation
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 10,
    },
  });
  const keyboardSensor = useSensor(KeyboardSensor);

  // Disable sensors when applying template
  const sensors = useSensors(
    ...(isApplying ? [] : [pointerSensor, keyboardSensor]),
  );

  // Clear pending placement when clicking outside
  useEffect(() => {
    if (!pendingPlacement || mode !== ELayoutManagerMode.EDIT) {
      return;
    }

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Check if click is inside a BlockAddButton, widget palette, or draggable item
      const isInsideAddButton = target.closest(
        '[class*="TSMED-BlockAddButton"]',
      );
      const isInsidePalette =
        target.closest('[class*="WidgetPalette"]') ||
        target.closest('[data-palette-widget]');
      const isInsideDraggableItem = target.closest(
        '[class*="TSMED-draggableItem"]',
      );

      // Don't clear if clicking inside add button, palette, or draggable item
      if (isInsideAddButton || isInsidePalette || isInsideDraggableItem) {
        return;
      }

      // Defer clearing to allow click handlers to process first
      // This prevents race condition where outside handler clears before widget placement completes
      setTimeout(() => {
        setPendingPlacement(null);
      }, 0);
    };

    // Add listener with capture phase to catch clicks early
    document.addEventListener('mousedown', handleClickOutside, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside, true);
    };
  }, [pendingPlacement, mode, setPendingPlacement]);

  // Clear pending placement when drag starts (user switched to drag interaction)
  useEffect(() => {
    if (isDragging && pendingPlacement) {
      setPendingPlacement(null);
    }
  }, [isDragging, pendingPlacement, setPendingPlacement]);

  const ctxValue: LayoutEditingContextProps = useMemo(
    () => ({
      mode,
      selectedWidgetId,
      setSelectedWidgetId,
      selectedPaletteWidget,
      setSelectedPaletteWidget,
      handleTapPlace,
      handleTapReplace,
      pendingPlacement,
      setPendingPlacement,
      handleDeleteWidget,
      handleTapSwap,
      showToast,
    }),
    [
      mode,
      selectedWidgetId,
      setSelectedWidgetId,
      selectedPaletteWidget,
      setSelectedPaletteWidget,
      pendingPlacement,
      setPendingPlacement,
      handleTapPlace,
      handleTapReplace,
      handleDeleteWidget,
      handleTapSwap,
      showToast,
    ],
  );

  return (
    <LayoutEditingContext.Provider value={ctxValue}>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
        autoScroll={{ enabled: true }}
      >
        {children}

        <DeleteZoneOverlay visible={isDragging} />

        <DragOverlay
          modifiers={[centerCursorModifier, restrictToWindowEdges]}
          adjustScale={false}
          style={{
            zIndex: 99999,
            cursor: 'grabbing',
          }}
          dropAnimation={null}
        >
          {activeId && (
            <DragPreview
              id={activeId}
              type={
                widgetRegistry.has(
                  extractBaseWidgetType(activeId as string) as WidgetType,
                )
                  ? 'widget-prototype'
                  : 'widget-instance'
              }
              displayName={
                typeof activeId === 'string'
                  ? activeId
                      .split('-')[0]
                      .replace(/-/g, ' ')
                      .replace(/\b\w/g, l => l.toUpperCase())
                  : undefined
              }
            />
          )}
        </DragOverlay>

        <ToastComponent />
      </DndContext>
    </LayoutEditingContext.Provider>
  );
};
